import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ChevronDown,
  ChevronUp,
  KeyRound,
  LogOut,
  Menu,
  User,
} from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useUISettings } from '../context/uiSettingsContext';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useRoleSwitcher } from '../hooks/useRoleSwitcher';
import { fetchMenu, setFilteredMenus } from '../modules/menu/menuApi';
import ChangePasswordModal from '../modules/home/changePassword/ChangePasswordModal';

import { formatTitle, getValidRoute } from '../utils/common';

import BrandLink from './BrandLink';
import LucideIcon from './LucideIcon';
import FloatingSubmenu from './FloatingSubmenu';
import UserAvatar from './UserAvatar';

import type { SidebarProps } from '../model';
import type { MenuItem, MenuState } from '../modules/menu/model';
import type { RootState } from '../store';

const Sidebar: React.FC<SidebarProps> = ({
  onRequestLogout,
  isMobileOpen = false,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const { isSidebarCollapsed, toggleSidebar } = useUISettings();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const profileRef = useRef<HTMLDivElement>(null);

  const [expandedMenuIds, setExpandedMenuIds] = useState<string[]>([]);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const hasHydratedRef = useRef(false);

  // Hover state for collapsed menu
  const [hoveredMenuId, setHoveredMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { menuItems, filteredMenus, isInitialized, loading }: MenuState =
    useAppSelector((state) => state.menu);
  const arrFilteredMenu = filteredMenus;

  const details = useAppSelector(
    (state: RootState) => state.profile.details
  ) as any;
  const userName = details?.name ?? '';
  const userEmail = details?.email ?? '';
  const profileImageUrl = details?.profileImageUrl;
  const { activeRoleCode } = useRoleSwitcher();

  const rawPermissions = (
    useAppSelector((state) => state.profile.details) as any
  )?.permissions;
  const permissions = useMemo(
    () => (Array.isArray(rawPermissions) ? rawPermissions : []),
    [rawPermissions]
  );

  const isCollapsed = isSidebarCollapsed;

  useEffect(() => {
    if (!isInitialized && !loading) {
      dispatch(fetchMenu());
    }
  }, [dispatch, isInitialized]);

  // Static menu items to add to sidebar
  const getStaticMenuItems = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [];

    // No longer adding My Invitations as a standalone menu
    // It will be added as a child of Trainings menu below

    return items;
  }, [activeRoleCode, permissions]);

  const computedFilteredMenus = useMemo(() => {
    const isHBAdmin = activeRoleCode === 'HBADMIN';
    const backendMenus = menuItems.filter((menu) => {
      const isAllowed = isHBAdmin || !permissions || permissions.length === 0; // Simplified for now, or use complex logic
      const hasChildren = (menu.submenus?.length ?? 0) > 0;
      const isSitemap = menu.menuCode === 'SITEMAP';
      const isStandalone = !!menu.routePath;

      // Hide dashboard for EMP users
      const isDashboardForEmp =
        activeRoleCode === 'EMP' &&
        (menu.routePath === '/dashboard' ||
          menu.routePath?.includes('dashboard') ||
          menu.menuName?.toLowerCase().includes('dashboard'));

      return (
        isAllowed &&
        (hasChildren || isStandalone || isSitemap) &&
        !isDashboardForEmp
      );
    });

    // Merge static menus with backend menus
    return [...backendMenus, ...getStaticMenuItems];
  }, [menuItems, permissions, activeRoleCode, getStaticMenuItems]);

  useEffect(() => {
    if (
      JSON.stringify(computedFilteredMenus) !== JSON.stringify(filteredMenus)
    ) {
      dispatch(setFilteredMenus(computedFilteredMenus));
    }
  }, [computedFilteredMenus, filteredMenus, dispatch]);

  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenuIds([]);
      setShowProfileDrawer(false);
    } else {
      // Clear hover state if we expand sidebar
      setHoveredMenuId(null);
      setMenuPosition(null);
    }
  }, [isCollapsed]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setShowProfileDrawer(false);
      }
    }
    if (showProfileDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDrawer]);

  const toggleMenu = (menuId: string) => {
    if (isCollapsed) {
      return;
    }
    setExpandedMenuIds((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isSubMenuActive = (subMenus: MenuItem[]) =>
    subMenus.some((sub) => {
      const route = getValidRoute(sub.routePath);
      return route ? location.pathname.startsWith(route) : false;
    });

  const activeParentMenuId = useMemo(() => {
    const active = arrFilteredMenu.find((menu) =>
      menu.submenus?.some(
        (sub) => sub.routePath && location.pathname.startsWith(sub.routePath)
      )
    );
    return active?.menuId ?? null;
  }, [arrFilteredMenu, location.pathname]);

  const menuSignature = useMemo(
    () => arrFilteredMenu.map((menu) => menu.menuId).join(','),
    [arrFilteredMenu]
  );

  useEffect(() => {
    hasHydratedRef.current = false;
  }, [menuSignature]);

  useEffect(() => {
    if (isCollapsed) return;
    if (hasHydratedRef.current) return;
    const nextExpanded = activeParentMenuId ? [activeParentMenuId] : [];
    setExpandedMenuIds(nextExpanded);
    hasHydratedRef.current = true;
  }, [activeParentMenuId, isCollapsed, menuSignature]);

  const handleProfile = () => {
    setShowProfileDrawer(false);
    navigate('/my-profile');
  };

  const handleChangePassword = () => {
    setShowProfileDrawer(false);
    setShowChangePasswordModal(true);
  };

  // Hover Handlers
  const handleIconMouseEnter = (
    menuId: string,
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (!isCollapsed) return;

    // Check if menu has children
    const menu = menuItems.find((m) => m.menuId === menuId);
    if (!menu || !menu.submenus || menu.submenus.length === 0) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.top, left: rect.right });
    setHoveredMenuId(menuId);
  };

  const handleIconMouseLeave = () => {
    if (!isCollapsed) return;

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMenuId(null);
      setMenuPosition(null);
    }, 150); // 150ms delay to allow moving to submenu
  };

  const keepSubmenuOpen = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const isActiveLink = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <aside
        className={[
          'flex flex-col shrink-0',
          'bg-white dark:bg-neutral-950',
          'border-r border-neutral-200 dark:border-neutral-800',
          'transition-all duration-300',
          // On desktop: relative positioning (normal flow)
          'md:relative md:translate-x-0',
          // On mobile: fixed full-height drawer that slides in from left
          'fixed top-0 left-0 h-full',
          isMobileOpen
            ? 'translate-x-0 z-50'
            : '-translate-x-full z-50 md:z-auto',
          isCollapsed ? 'w-[72px]' : 'w-(--sidebar-width)',
        ].join(' ')}
      >
        <div
          className={`shrink-0 flex items-center ${
            isCollapsed
              ? 'flex-col px-2 py-2 gap-1'
              : 'h-(--header-height) px-4 justify-between'
          }`}
        >
          {!isCollapsed && <BrandLink showText={true} />}

          <button
            type="button"
            onClick={toggleSidebar}
            className={`flex items-center justify-center rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors ${
              isCollapsed ? 'w-full py-2.5' : 'w-8 h-8'
            }`}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <div
          className={`flex-1 overflow-y-auto overflow-x-hidden py-2 ${isCollapsed ? 'scrollbar-hide' : 'custom-scroll'}`}
        >
          <div className="space-y-1 px-2">
            {arrFilteredMenu.map((menu: MenuItem) => {
              const menuLabel = formatTitle(menu.menuName);
              const menuIcon = (menu.icon ?? '').trim();
              const hasChildren = (menu.submenus?.length ?? 0) > 0;
              const isExpanded = expandedMenuIds.includes(menu.menuId);
              const hasActiveSubItem =
                hasChildren && isSubMenuActive(menu.submenus);

              const isSitemap = menu.menuCode === 'SITEMAP';
              const navLinkTarget =
                getValidRoute(menu.routePath) ??
                (isSitemap ? '/sitemap' : null);
              const shouldRenderLink = Boolean(navLinkTarget) && !hasChildren;

              const baseItemClass = `w-full flex items-center py-2.5 rounded-lg transition-colors focus:outline-none ${
                isCollapsed ? 'justify-center px-0' : 'justify-between px-3'
              }`;
              const activeItemClass =
                'bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400';
              const inactiveItemClass =
                'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900';

              return (
                <div
                  key={menu.menuId}
                  onMouseEnter={(e) => handleIconMouseEnter(menu.menuId, e)}
                  onMouseLeave={handleIconMouseLeave}
                >
                  {shouldRenderLink ? (
                    <NavLink
                      to={navLinkTarget as string}
                      onClick={onMobileClose}
                      className={({ isActive }) =>
                        `${baseItemClass} ${
                          isActive ? activeItemClass : inactiveItemClass
                        }`
                      }
                      title={isCollapsed ? menuLabel : undefined}
                    >
                      <div
                        className={`flex items-center ${
                          isCollapsed ? 'justify-center' : 'gap-3'
                        } min-w-0`}
                      >
                        {menuIcon ? (
                          <LucideIcon
                            iconName={menuIcon}
                            className="text-current"
                            size={isCollapsed ? 22 : 20}
                          />
                        ) : (
                          <span className="inline-block shrink-0 w-5 h-5" />
                        )}
                        {!isCollapsed && (
                          <span className="text-sm truncate">{menuLabel}</span>
                        )}
                      </div>
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleMenu(menu.menuId)}
                      className={`${baseItemClass} ${
                        hasActiveSubItem ? activeItemClass : inactiveItemClass
                      }`}
                      title={isCollapsed ? menuLabel : undefined}
                    >
                      <div
                        className={`flex items-center ${
                          isCollapsed ? 'justify-center' : 'gap-3'
                        } min-w-0`}
                      >
                        {menuIcon ? (
                          <LucideIcon
                            iconName={menuIcon}
                            className="text-current"
                            size={isCollapsed ? 22 : 20}
                          />
                        ) : (
                          <span className="inline-block shrink-0 w-5 h-5" />
                        )}
                        {!isCollapsed && (
                          <span className="text-sm truncate">{menuLabel}</span>
                        )}
                      </div>
                      {!isCollapsed && hasChildren && (
                        <span className="shrink-0">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </span>
                      )}
                    </button>
                  )}

                  {!isCollapsed && hasChildren && (
                    <div
                      className={`mt-1 ml-8 mr-2 space-y-1 overflow-hidden transition-all duration-200 ease-out ${
                        isExpanded
                          ? 'max-h-[1000px] opacity-100'
                          : 'max-h-0 opacity-0 pointer-events-none'
                      }`}
                    >
                      {menu.submenus.map((subMenu: MenuItem) => {
                        const subLabel = formatTitle(subMenu.menuName);
                        const subRoute = getValidRoute(subMenu.routePath);
                        const isDisabled = !subRoute;
                        return subRoute ? (
                          <NavLink
                            key={subMenu.menuId}
                            to={subRoute}
                            className={({ isActive }) =>
                              `w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors focus:outline-none ${
                                isActive
                                  ? 'bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400'
                                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900 hover:text-neutral-900 dark:hover:text-white'
                              }`
                            }
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                            <span className="truncate">{subLabel}</span>
                          </NavLink>
                        ) : (
                          <div
                            key={subMenu.menuId}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                              isDisabled
                                ? 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                                : 'text-neutral-600 dark:text-neutral-400'
                            }`}
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                            <span className="truncate">{subLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div
          ref={profileRef}
          className="relative mt-auto border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 min-h-(--listing-footer-height) py-2 flex items-center"
        >
          <button
            type="button"
            onClick={() =>
              setShowProfileDrawer((prev) => (!isCollapsed ? !prev : false))
            }
            className={`w-full h-full px-3 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors focus:outline-none ${
              isCollapsed
                ? 'flex items-center justify-center'
                : 'flex items-center'
            }`}
          >
            {isCollapsed ? (
              <UserAvatar
                src={profileImageUrl}
                alt={userName || userEmail || 'User'}
                size="md"
                objectPosition="50% 30%"
                className="text-white text-sm"
                style={{ backgroundColor: 'var(--color-brand-600)' }}
              />
            ) : (
              <div className="flex items-center gap-3 overflow-hidden">
                <UserAvatar
                  src={profileImageUrl}
                  alt={userName || userEmail || 'User'}
                  size="md"
                  objectPosition="50% 30%"
                  className="text-white text-sm shrink-0"
                  style={{ backgroundColor: 'var(--color-brand-600)' }}
                />
                <div className="flex-1 min-w-0 text-left overflow-hidden">
                  <div
                    className="text-sm font-bold truncate"
                    style={{ color: 'var(--color-brand-600)' }}
                  >
                    {userName}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {userEmail}
                  </div>
                </div>
                <ChevronUp
                  className={`w-4 h-4 text-neutral-400 shrink-0 transition-transform ${
                    showProfileDrawer ? 'rotate-180' : ''
                  }`}
                />
              </div>
            )}
          </button>

          {showProfileDrawer && !isCollapsed && (
            <div className="absolute bottom-full left-0 right-0 mb-2 mx-2">
              <div className="bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg overflow-hidden">
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-start gap-3 overflow-hidden">
                    <div className="relative w-14 h-14 shrink-0">
                      <UserAvatar
                        src={profileImageUrl}
                        alt={userName || userEmail || 'User'}
                        size="lg"
                        objectPosition="50% 30%"
                        className="bg-brand-600 dark:bg-brand-500 text-white font-medium"
                      />
                      {/* Bottom-left glow indicator */}
                      <div
                        className="absolute -bottom-1 -left-1 w-3 h-3 bg-brand-400 rounded-full border-2 border-white dark:border-neutral-950 animate-pulse"
                        style={{ boxShadow: '0 0 8px var(--color-brand-400)' }}
                      />
                      {/* Top-right glow indicator */}
                      <div
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-brand-500 rounded-full border-2 border-white dark:border-neutral-950"
                        style={{ boxShadow: '0 0 6px var(--color-brand-500)' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white mb-0.5 truncate">
                        {userName}
                      </div>
                      <div className="text-xs text-neutral-600 dark:text-neutral-400 mb-1 truncate">
                        {userEmail}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-1 border-t border-neutral-200 dark:border-neutral-800">
                  <button
                    type="button"
                    onClick={handleProfile}
                    className="w-full px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded transition-colors flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    className="w-full px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded transition-colors flex items-center gap-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    <span>Change Password</span>
                  </button>
                </div>

                <div className="p-1 border-t border-neutral-200 dark:border-neutral-800">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileDrawer(false);
                      onRequestLogout();
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-950 rounded transition-colors flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {hoveredMenuId && isCollapsed && (
          <FloatingSubmenu
            menu={menuItems.find((m) => m.menuId === hoveredMenuId)!}
            position={menuPosition}
            onMouseEnter={keepSubmenuOpen}
            onMouseLeave={handleIconMouseLeave}
            isActiveLink={isActiveLink}
          />
        )}
      </aside>
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  );
};

export default Sidebar;
